import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Typography, 
  message, 
  Checkbox,
  Alert
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { users } from '../../api/mockData';
import './index.css';

const { Title, Text } = Typography;

const AuthPage = () => {
  const [form] = Form.useForm();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    
    try {
      // 模拟 API 调用延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (isLogin) {
        // 登录逻辑
        const user = users.find(
          u => u.username === values.username && u.password === values.password
        );
        
        if (user) {
          // 保存用户信息到 localStorage
          const { password, ...userWithoutPassword } = user;
          localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
          
          message.success('登录成功！');
          navigate('/');
        } else {
          setError('用户名或密码错误');
        }
      } else {
        // 注册逻辑
        const userExists = users.some(u => u.username === values.username);
        
        if (userExists) {
          setError('用户名已存在');
        } else {
          // 创建新用户
          const newUser = {
            id: users.length + 1,
            username: values.username,
            password: values.password,
            name: values.nickname || values.username,
            avatar: 'https://randomuser.me/api/portraits/men/' + (users.length + 1) + '.jpg',
            intro: '新用户',
            details: '欢迎新用户！',
            email: values.email || '未设置邮箱',
            techStack: '暂无',
            interests: '暂无',
            github: '#',
            linkedin: '#',
            personalHomepage: '#',
            role: 'user'
          };
          
          // 将新用户添加到users数组中
          users.push(newUser);
          
          // 保存当前用户信息到 localStorage（注册后自动登录）
          const { password, ...userWithoutPassword } = newUser;
          localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
          
          message.success('注册成功！');
          setIsLogin(true);
          form.resetFields();
          
          // 注册成功后跳转到首页
          navigate('/');
        }
      }
    } catch (err) {
      setError('操作失败，请重试');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMode = () => {
    setIsLogin(!isLogin);
    form.resetFields();
    setError('');
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject('请输入密码');
    }
    if (value.length < 6) {
      return Promise.reject('密码至少6位');
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) {
        return Promise.resolve();
      }
      return Promise.reject('两次输入的密码不一致');
    },
  });

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-header">
            <BookOutlined className="auth-logo" />
            <Title level={3} className="auth-title">
              {isLogin ? '欢迎回来' : '加入我们'}
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {isLogin ? '登录您的账户继续探索' : '创建新账户开始写作之旅'}
            </Text>
          </div>
          
          <div className="auth-form">
            {error && (
              <Alert 
                message={error} 
                type="error" 
                showIcon 
                className="error-message"
              />
            )}
            
            <Form
              form={form}
              name="auth-form"
              onFinish={handleSubmit}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { max: 20, message: '用户名最多20个字符' }
                ]}
                className="form-item"
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="用户名" 
                />
              </Form.Item>
              
              {!isLogin && (
                <>
                  <Form.Item
                    name="nickname"
                    rules={[
                      { required: true, message: '请输入昵称' },
                      { max: 20, message: '昵称最多20个字符' }
                    ]}
                    className="form-item"
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      placeholder="昵称" 
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="email"
                    rules={[
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                    className="form-item"
                  >
                    <Input 
                      prefix={<MailOutlined />} 
                      placeholder="邮箱（可选）" 
                    />
                  </Form.Item>
                </>
              )}
              
              <Form.Item
                name="password"
                rules={[{ validator: validatePassword }]}
                className="form-item"
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="密码" 
                />
              </Form.Item>
              
              {!isLogin && (
                <Form.Item
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: '请确认密码' },
                    validateConfirmPassword
                  ]}
                  className="form-item"
                >
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="确认密码" 
                  />
                </Form.Item>
              )}
              
              {isLogin && (
                <div className="form-footer">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox className="remember-me">记住我</Checkbox>
                  </Form.Item>
                  <Text className="forgot-password">忘记密码？</Text>
                </div>
              )}
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  className="submit-button"
                >
                  {isLogin ? '登录' : '注册'}
                </Button>
              </Form.Item>
              
              <div className="auth-switch">
                <Text>
                  {isLogin ? '还没有账户？' : '已有账户？'}
                  <span className="switch-link" onClick={handleSwitchMode}>
                    {isLogin ? '立即注册' : '立即登录'}
                  </span>
                </Text>
              </div>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;